/* Audio page (PPV voice notes, bigdayz.new ADR 0068) — pure helpers. */

import type { AudioPolicyBody, Voice, VoiceNoteTestBody } from '../../../../api/client';

export const SECS = ['voice', 'notes', 'test'] as const;
export type Sec = (typeof SECS)[number];

/** Languages offered as chips. Stored as ISO 639-1 codes; names come from Intl. */
export const LANGUAGES = ['en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'pl', 'ru', 'uk'] as const;
/** The ones that can be turned on today; the rest show as "Coming soon". */
export const AVAILABLE_LANGUAGES: readonly string[] = ['en'];

export const MAX_COOLDOWN = 50;
export const MAX_TONE_NOTES = 1000;
export const MAX_CLONE_FILES = 3;
export const MAX_CLONE_MB = 10;
/** File picker filter for clone samples. Extensions are listed because audio/* alone hides
 *  .mp4/.mov (video/*) and, on macOS, .ogg/.opus (no audio type registered). Must match
 *  AUDIO_EXTENSIONS in backend/routes/audio.py. */
export const CLONE_ACCEPT = 'audio/*,.mp3,.wav,.m4a,.aac,.flac,.ogg,.oga,.opus,.webm,.weba,.mp4,.mov';

export const EMPTY_POLICY: AudioPolicyBody = {
  voice_id: null,
  custom_voice_id: null,
  voice_notes_enabled: false,
  voice_note_cooldown: null,
  normal_tone_notes: '',
  hot_tone_notes: '',
  allowed_languages: ['en'],
};

/** The voice the policy points at, as a picker key ("stock:<id>" / "custom:<id>"). */
export const selectedKey = (p: AudioPolicyBody): string | null =>
  p.voice_id ? `stock:${p.voice_id}` : p.custom_voice_id ? `custom:${p.custom_voice_id}` : null;

export const voiceKey = (v: Voice) => `${v.kind}:${v.id}`;

export const selectVoice = (v: Voice): Pick<AudioPolicyBody, 'voice_id' | 'custom_voice_id'> =>
  v.kind === 'stock' ? { voice_id: v.id, custom_voice_id: null } : { voice_id: null, custom_voice_id: v.id };

export function languageName(code: string, locale: string): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'language' }).of(code);
    return name ? name.charAt(0).toLocaleUpperCase(locale) + name.slice(1) : code;
  } catch {
    return code;
  }
}

/** Why the draft can't be saved, or null. */
export function policyProblem(p: AudioPolicyBody): 'cooldown' | 'tone' | null {
  const c = p.voice_note_cooldown;
  if (c !== null && (!Number.isInteger(c) || c < 0 || c > MAX_COOLDOWN)) return 'cooldown';
  if (p.normal_tone_notes.length > MAX_TONE_NOTES || p.hot_tone_notes.length > MAX_TONE_NOTES) return 'tone';
  return null;
}

/** Why voice notes won't go out with this policy, most important first (or null). */
export function offReason(p: AudioPolicyBody, voices: Voice[] | null): 'disabled' | 'noVoice' | 'voiceNotReady' | 'noLanguages' | null {
  if (!p.voice_notes_enabled) return 'disabled';
  const key = selectedKey(p);
  if (!key) return 'noVoice';
  const v = voices?.find((x) => voiceKey(x) === key);
  if (voices && v && v.status !== 'ready') return 'voiceNotReady';
  if (!p.allowed_languages.length) return 'noLanguages';
  return null;
}

export const MAX_TEST_TEXT = 1000;

export interface TestDraft {
  text: string;
  voice_register: VoiceNoteTestBody['voice_register'];
}

export const EMPTY_TEST: TestDraft = { text: '', voice_register: 'hot' };

/** A live test runs with the page's current draft (saved or not). */
export const testBody = (p: AudioPolicyBody, d: TestDraft): VoiceNoteTestBody => ({
  ...d,
  voice_id: p.voice_id,
  custom_voice_id: p.custom_voice_id,
  normal_tone_notes: p.normal_tone_notes,
  hot_tone_notes: p.hot_tone_notes,
  allowed_languages: p.allowed_languages,
});

/** Skip reasons the brain / console report, mapped to i18n keys under au.skip. */
export const SKIP_REASONS = [
  'language_not_allowed', 'no_allowed_languages', 'writer_failed', 'empty_script',
  'script_too_long', 'banned_words', 'synthesis_failed', 'audio_too_long',
] as const;
export const skipKey = (reason: string | null) =>
  reason && (SKIP_REASONS as readonly string[]).includes(reason) ? reason : 'other';
